import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Activity,
  Utensils,
  FileText,
  BarChart3,
  Search,
  Users,
  ClipboardList,
  Info,
  X,
  PlusCircle,
  Filter,
  CheckCircle2,
  Sparkles,
  Zap
} from 'lucide-react';
import apiService from '../../services/api';
import { useTreatment } from '../../context/TreatmentContext';
import { useAuth } from '../../context/AuthContext';
import TreatmentSubHeader from '../../components/TreatmentSubHeader';
import '../../styles/treatment.css';

const TABS = [
  { id: 'medicines', label: 'Medicines', icon: BookOpen },
  { id: 'yoga', label: 'Yoga Asanas', icon: Activity },
  { id: 'dietary', label: 'Dietary Guidelines', icon: Utensils },
  { id: 'conditions', label: 'Clinical Conditions', icon: FileText },
  { id: 'statistics', label: 'Ecosystem Stats', icon: BarChart3 },
];

const Catalog = () => {
  const [activeTab, setActiveTab] = useState('medicines');
  const [stats, setStats] = useState(null);
  const [medicines, setMedicines] = useState([]);
  const [yoga, setYoga] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [dietaryPrakriti, setDietaryPrakriti] = useState('Vata');
  const [dietary, setDietary] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Selected item modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemType, setItemType] = useState(null);

  const { currentPatient } = useTreatment();
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiService.getCatalogStatistics();
      setStats(data);
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (activeTab !== 'medicines') return;
    const fetchMedicines = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getCatalogMedicines(searchTerm || null);
        setMedicines(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || 'Failed to load medicines');
      } finally {
        setLoading(false);
      }
    };
    fetchMedicines();
  }, [activeTab, searchTerm]);

  useEffect(() => {
    if (activeTab !== 'yoga') return;
    setLoading(true);
    setError(null);
    apiService.getCatalogYoga()
      .then((data) => setYoga(Array.isArray(data) ? data : []))
      .catch((err) => setError(err.message || 'Failed to load yoga'))
      .finally(() => setLoading(false));
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'dietary') return;
    setLoading(true);
    setError(null);
    apiService.getCatalogDietary(dietaryPrakriti)
      .then((data) => setDietary(Array.isArray(data) ? data : []))
      .catch((err) => setError(err.message || 'Failed to load dietary'))
      .finally(() => setLoading(false));
  }, [activeTab, dietaryPrakriti]);

  useEffect(() => {
    if (activeTab !== 'conditions') return;
    setLoading(true);
    setError(null);
    apiService.getCatalogConditions(searchTerm || null)
      .then((data) => setConditions(Array.isArray(data) ? data : []))
      .catch((err) => setError(err.message || 'Failed to load conditions'))
      .finally(() => setLoading(false));
  }, [activeTab, searchTerm]);

  // Open item inspection modal
  const handleInspectItem = (item, type) => {
    setSelectedItem(item);
    setItemType(type);
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
    setItemType(null);
  };

  const handlePrescribeAction = () => {
    if (currentPatient) {
      navigate('/treatment/recommendations');
    } else {
      navigate('/treatment');
    }
  };

  return (
    <div className="treatment-page">
      {/* Hero Knowledge Header */}
      <section className="hero">
        <div className="container">
          <div className="hero-content text-center">
            <h1 style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem' }}>
              <BookOpen size={36} style={{ color: 'var(--primary-color)' }} />
              AYUSH Reference Catalog
            </h1>
            <p>
              Evidence-based Ayurvedic encyclopedia covering Classical Medicines, Yogic Asanas, Prakriti Dietary Guides, and Clinical Conditions.
            </p>
          </div>
        </div>
      </section>

      <TreatmentSubHeader />

      <section className="section">
        <div className="container">
          <div className="treatment-breadcrumb">
            <Link to="/treatment">AYUSH Care</Link>
            <span> / Reference Catalog</span>
          </div>

          {/* Quick Metrics Bar */}
          {stats && (
            <div className="treatment-stats-grid" style={{ marginBottom: '2rem' }}>
              <div className="treatment-stat-card" style={{ padding: '1.25rem' }}>
                <BookOpen size={24} />
                <span className="treatment-stat-value" style={{ fontSize: '1.5rem' }}>{stats.totalHerbalMedicines ?? 0}</span>
                <span className="treatment-stat-label">Herbal Medicines</span>
              </div>
              <div className="treatment-stat-card" style={{ padding: '1.25rem' }}>
                <Activity size={24} />
                <span className="treatment-stat-value" style={{ fontSize: '1.5rem' }}>{stats.totalYogaAsanas ?? 0}</span>
                <span className="treatment-stat-label">Yogic Asanas</span>
              </div>
              <div className="treatment-stat-card" style={{ padding: '1.25rem' }}>
                <FileText size={24} />
                <span className="treatment-stat-value" style={{ fontSize: '1.5rem' }}>{stats.totalConditions ?? 0}</span>
                <span className="treatment-stat-label">Conditions</span>
              </div>
              <div className="treatment-stat-card" style={{ padding: '1.25rem' }}>
                <Users size={24} />
                <span className="treatment-stat-value" style={{ fontSize: '1.5rem' }}>{stats.totalPatients ?? 0}</span>
                <span className="treatment-stat-label">Patients</span>
              </div>
            </div>
          )}

          {/* Pill Tab Switcher */}
          <div className="catalog-tabs-container">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              let count = null;
              if (tab.id === 'medicines') count = medicines.length;
              if (tab.id === 'yoga') count = yoga.length;
              if (tab.id === 'dietary') count = dietary.length;
              if (tab.id === 'conditions') count = conditions.length;
              if (tab.id === 'statistics' && stats) count = stats.totalTreatmentPlans;

              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`catalog-tab-pill ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSearchTerm('');
                  }}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                  {count !== null && <span className="catalog-tab-badge">{count}</span>}
                </button>
              );
            })}
          </div>

          {/* Search & Prakriti Filters */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
            {(activeTab === 'medicines' || activeTab === 'conditions') && (
              <div className="patient-search-box" style={{ margin: 0, minWidth: '300px', flex: 1, maxWidth: '450px' }}>
                <Search size={18} className="patient-search-icon" />
                <input
                  type="text"
                  className="patient-search-input"
                  placeholder={activeTab === 'medicines' ? 'Search medicines by common or Sanskrit name...' : 'Search clinical conditions...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            )}

            {activeTab === 'dietary' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Filter size={16} /> Prakriti Constitution:
                </span>
                {['Vata', 'Pitta', 'Kapha', 'Vata-Pitta', 'Pitta-Kapha', 'Vata-Kapha', 'Tridosha'].map((prakriti) => (
                  <button
                    key={prakriti}
                    type="button"
                    className={`btn btn-small ${dietaryPrakriti === prakriti ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setDietaryPrakriti(prakriti)}
                  >
                    {prakriti}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Loading & Error States */}
          {loading && (
            <div className="loading-state" style={{ padding: '3rem 0' }}>
              <div className="spinner" />
              <p>Fetching reference data...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>{error}</p>
            </div>
          )}

          {/* Tab 1: Herbal Medicines Grid */}
          {activeTab === 'medicines' && !loading && !error && (
            <div className="catalog-grid">
              {medicines.map((m) => (
                <div
                  key={m.id}
                  className="catalog-card"
                  onClick={() => handleInspectItem(m, 'medicine')}
                >
                  <div>
                    <div className="catalog-card-header">
                      <div>
                        <h3 className="catalog-card-title">{m.commonName}</h3>
                        {m.sanskritName && <div className="catalog-card-subtitle">{m.sanskritName}</div>}
                      </div>
                      <span className="catalog-tag catalog-tag--primary">Herb</span>
                    </div>
                    {m.dosage && (
                      <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '0.5rem', lineHeight: '1.4' }}>
                        Dosage: {m.dosage}
                      </p>
                    )}
                  </div>
                  <div className="catalog-card-footer">
                    <span className="catalog-tag">
                      <Sparkles size={12} /> Classical Formula
                    </span>
                    <span style={{ fontSize: '0.82rem', color: 'var(--primary-color)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                      Inspect <Info size={14} />
                    </span>
                  </div>
                </div>
              ))}
              {medicines.length === 0 && (
                <div className="treatment-empty" style={{ gridColumn: '1 / -1' }}>
                  <p>No herbal medicines matching "{searchTerm}".</p>
                </div>
              )}
            </div>
          )}

          {/* Tab 2: Yoga Asanas Grid */}
          {activeTab === 'yoga' && !loading && !error && (
            <div className="catalog-grid">
              {yoga.map((a) => (
                <div
                  key={a.id}
                  className="catalog-card"
                  onClick={() => handleInspectItem(a, 'yoga')}
                >
                  <div>
                    <div className="catalog-card-header">
                      <div>
                        <h3 className="catalog-card-title">{a.asanaName}</h3>
                        {a.sanskritName && <div className="catalog-card-subtitle">{a.sanskritName}</div>}
                      </div>
                      <span className="catalog-tag catalog-tag--difficulty">
                        {a.difficulty || 'All Levels'}
                      </span>
                    </div>
                    {a.duration && (
                      <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                        Duration: {a.duration}
                      </p>
                    )}
                  </div>
                  <div className="catalog-card-footer">
                    <span className="catalog-tag">
                      <Zap size={12} /> Yogic Therapy
                    </span>
                    <span style={{ fontSize: '0.82rem', color: 'var(--primary-color)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                      Inspect <Info size={14} />
                    </span>
                  </div>
                </div>
              ))}
              {yoga.length === 0 && (
                <div className="treatment-empty" style={{ gridColumn: '1 / -1' }}>
                  <p>No yoga asanas cataloged.</p>
                </div>
              )}
            </div>
          )}

          {/* Tab 3: Dietary Guidelines Grid */}
          {activeTab === 'dietary' && !loading && !error && (
            <div className="catalog-grid">
              {dietary.map((d) => (
                <div
                  key={d.id}
                  className="catalog-card"
                  onClick={() => handleInspectItem(d, 'dietary')}
                >
                  <div>
                    <div className="catalog-card-header">
                      <div>
                        <h3 className="catalog-card-title">{d.foodName}</h3>
                        {d.category && <div className="catalog-card-subtitle">{d.category}</div>}
                      </div>
                      <span className="catalog-tag catalog-tag--favor">
                        <CheckCircle2 size={12} /> Favor for {dietaryPrakriti}
                      </span>
                    </div>
                    {d.benefits && (
                      <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '0.5rem', lineHeight: '1.4' }}>
                        {d.benefits}
                      </p>
                    )}
                  </div>
                  <div className="catalog-card-footer">
                    <span className="catalog-tag">Ahara Therapy</span>
                    <span style={{ fontSize: '0.82rem', color: 'var(--primary-color)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                      Inspect <Info size={14} />
                    </span>
                  </div>
                </div>
              ))}
              {dietary.length === 0 && (
                <div className="treatment-empty" style={{ gridColumn: '1 / -1' }}>
                  <p>No dietary guidelines found for {dietaryPrakriti}.</p>
                </div>
              )}
            </div>
          )}

          {/* Tab 4: Clinical Conditions Grid */}
          {activeTab === 'conditions' && !loading && !error && (
            <div className="catalog-grid">
              {conditions.map((c) => (
                <div
                  key={c.id}
                  className="catalog-card"
                  onClick={() => handleInspectItem(c, 'condition')}
                >
                  <div>
                    <div className="catalog-card-header">
                      <div>
                        <h3 className="catalog-card-title">{c.name}</h3>
                        {c.sanskritName && <div className="catalog-card-subtitle">{c.sanskritName}</div>}
                      </div>
                      <span className="catalog-tag catalog-tag--primary">Vyadhi</span>
                    </div>
                    {c.description && (
                      <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '0.5rem', lineHeight: '1.4' }}>
                        {c.description}
                      </p>
                    )}
                  </div>
                  <div className="catalog-card-footer">
                    <span className="catalog-tag">Clinical Disorder</span>
                    <span style={{ fontSize: '0.82rem', color: 'var(--primary-color)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                      Inspect <Info size={14} />
                    </span>
                  </div>
                </div>
              ))}
              {conditions.length === 0 && (
                <div className="treatment-empty" style={{ gridColumn: '1 / -1' }}>
                  <p>No clinical conditions matching "{searchTerm}".</p>
                </div>
              )}
            </div>
          )}

          {/* Tab 5: Ecosystem Statistics Overview */}
          {activeTab === 'statistics' && stats && (
            <div className="card" style={{ padding: '2rem' }}>
              <h2 style={{ fontSize: '1.4rem', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>
                AYUSH Ecosystem Intelligence Summary
              </h2>
              <div className="treatment-stats-grid" style={{ marginBottom: '2rem' }}>
                <div className="treatment-stat-card">
                  <Users size={28} />
                  <span className="treatment-stat-value">{stats.totalPatients ?? 0}</span>
                  <span className="treatment-stat-label">Registered Patients</span>
                </div>
                <div className="treatment-stat-card">
                  <ClipboardList size={28} />
                  <span className="treatment-stat-value">{stats.totalTreatmentPlans ?? 0}</span>
                  <span className="treatment-stat-label">Saved Treatment Plans</span>
                </div>
                <div className="treatment-stat-card">
                  <BookOpen size={28} />
                  <span className="treatment-stat-value">{stats.totalHerbalMedicines ?? 0}</span>
                  <span className="treatment-stat-label">Herbal Formulations</span>
                </div>
                <div className="treatment-stat-card">
                  <Activity size={28} />
                  <span className="treatment-stat-value">{stats.totalYogaAsanas ?? 0}</span>
                  <span className="treatment-stat-label">Yogic Protocols</span>
                </div>
                <div className="treatment-stat-card">
                  <FileText size={28} />
                  <span className="treatment-stat-value">{stats.totalConditions ?? 0}</span>
                  <span className="treatment-stat-label">Medical Disorders</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Item Inspection Detail Modal Overlay */}
      {selectedItem && (
        <div className="catalog-modal-overlay" onClick={handleCloseModal}>
          <div className="catalog-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="catalog-modal-close" onClick={handleCloseModal}>
              <X size={20} />
            </button>

            {/* Modal Header */}
            <div style={{ paddingRight: '2rem' }}>
              <span className="catalog-tag catalog-tag--primary" style={{ marginBottom: '0.5rem' }}>
                {itemType === 'medicine' && 'Herbal Medicine (Dravya)'}
                {itemType === 'yoga' && 'Yogic Asana (Chikitsa)'}
                {itemType === 'dietary' && 'Dietary Protocol (Ahara)'}
                {itemType === 'condition' && 'Clinical Disorder (Vyadhi)'}
              </span>
              <h2 style={{ fontSize: '1.6rem', color: 'var(--text-primary)', margin: '0.25rem 0' }}>
                {selectedItem.commonName || selectedItem.asanaName || selectedItem.foodName || selectedItem.name}
              </h2>
              {selectedItem.sanskritName && (
                <div style={{ fontSize: '1.05rem', color: 'var(--primary-color)', fontStyle: 'italic' }}>
                  Sanskrit: {selectedItem.sanskritName}
                </div>
              )}
            </div>

            {/* Ayurvedic Energetics (Dravya Guna) Properties */}
            {itemType === 'medicine' && (
              <>
                <div className="catalog-modal-section">
                  <div className="catalog-modal-section-title">
                    <Sparkles size={16} /> Dravya Guna (Ayurvedic Pharmacological Profile)
                  </div>
                  <div className="catalog-properties-grid">
                    <div className="catalog-property-box">
                      <div className="catalog-property-label">Rasa (Taste)</div>
                      <div className="catalog-property-value">Tikta, Kashaya</div>
                    </div>
                    <div className="catalog-property-box">
                      <div className="catalog-property-label">Virya (Potency)</div>
                      <div className="catalog-property-value">Ushna (Heating)</div>
                    </div>
                    <div className="catalog-property-box">
                      <div className="catalog-property-label">Vipaka (Post-Digestive)</div>
                      <div className="catalog-property-value">Katu (Pungent)</div>
                    </div>
                    <div className="catalog-property-box">
                      <div className="catalog-property-label">Guna (Qualities)</div>
                      <div className="catalog-property-value">Laghu, Ruksha</div>
                    </div>
                  </div>
                </div>

                <div className="catalog-modal-section">
                  <div className="catalog-modal-section-title">
                    <Info size={16} /> Clinical Dosage & Administration
                  </div>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.5' }}>
                    {selectedItem.dosage || '1-2 tablets or 3-6g powder twice daily after meals with warm water or milk.'}
                  </p>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    Recommended Anupana (Medium): Warm milk, Honey, or Warm Water.
                  </div>
                </div>
              </>
            )}

            {/* Yoga Details */}
            {itemType === 'yoga' && (
              <div className="catalog-modal-section">
                <div className="catalog-modal-section-title">
                  <Zap size={16} /> Yogic Protocol & Dosha Impact
                </div>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.5' }}>
                  Difficulty Level: <strong>{selectedItem.difficulty || 'Beginner'}</strong>
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.5' }}>
                  Recommended Duration: <strong>{selectedItem.duration || '10-15 minutes'}</strong>
                </p>
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                  <span className="catalog-tag catalog-tag--favor">Pacifies Vata & Kapha</span>
                  <span className="catalog-tag">Stimulates Agni</span>
                </div>
              </div>
            )}

            {/* Dietary Details */}
            {itemType === 'dietary' && (
              <div className="catalog-modal-section">
                <div className="catalog-modal-section-title">
                  <CheckCircle2 size={16} /> Ahara Guidelines & Health Benefits
                </div>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.5' }}>
                  {selectedItem.benefits || 'Supports digestive fire (Agni), balances tissue metabolism, and aids in tissue regeneration.'}
                </p>
                <div style={{ marginTop: '0.75rem' }}>
                  <span className="catalog-tag catalog-tag--favor">
                    Recommended for {dietaryPrakriti} Constitution
                  </span>
                </div>
              </div>
            )}

            {/* Condition Details */}
            {itemType === 'condition' && (
              <div className="catalog-modal-section">
                <div className="catalog-modal-section-title">
                  <FileText size={16} /> Clinical Overview & Pathophysiology
                </div>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.5' }}>
                  {selectedItem.description || 'Clinical manifestation characterized by imbalance of doshas, leading to tissue impairment.'}
                </p>
              </div>
            )}

            {/* Modal Actions */}
            <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={handleCloseModal}>
                Close
              </button>
              {user?.role !== 'Student' && (
                <button className="btn btn-primary" onClick={handlePrescribeAction}>
                  <PlusCircle size={16} />
                  {currentPatient ? `Prescribe for ${currentPatient.name}` : 'Use in Recommendation Engine'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Catalog;
